/**
 * customize-variants.js — PDP Customize Button Variant-Toggle Component
 *
 * Defines <customize-button-variants>, a custom element that extends VariantInventory
 * (defined in assets/theme.js.liquid as the <variant-inventory> element). The extension
 * inherits the full toggle mechanism without duplicating a single line of its logic:
 *
 *   _onVariantChanged(event) {
 *     const variantId = event.detail.variant ? event.detail.variant["id"] : null;
 *     Array.from(this.children).forEach((item) =>
 *       item.toggleAttribute("hidden", variantId !== parseInt(item.getAttribute("data-variant-id"))));
 *   }
 *
 * VariantInventory listens for variant:change on document.forms[this.getAttribute("form")]
 * and toggles `hidden` on each direct child whose data-variant-id does not match the
 * newly selected variant ID. The <div data-variant-id> children in
 * snippets/customize-button-variants.liquid carry exactly this attribute.
 *
 * ── Why not reuse <variant-inventory> directly ────────────────────────────────
 *
 *   Reusing the same tag would work but would mislead the next maintainer: the element
 *   appearing around the customize button area would be named "variant-inventory", which
 *   has nothing to do with the inventory count display. A clearly-named element
 *   (<customize-button-variants>) makes the purpose self-evident in the markup.
 *
 * ── customElements.whenDefined ordering ──────────────────────────────────────
 *
 *   theme.js is a type="module" script (implicitly deferred). This file is loaded
 *   with defer (non-module). Both execute after DOM parsing completes, but execution
 *   order between them depends on network/parse timing.
 *
 *   Case A — theme.js runs first:
 *     variant-inventory is already registered. whenDefined('variant-inventory') resolves
 *     immediately. customize-button-variants is defined. The already-parsed
 *     <customize-button-variants> element in the DOM is upgraded; connectedCallback
 *     fires and the form listener is attached.
 *
 *   Case B — this file runs first:
 *     whenDefined('variant-inventory') returns a pending promise and queues the
 *     callback. When theme.js eventually registers variant-inventory, the promise
 *     resolves, customize-button-variants is defined, and the element is upgraded.
 *
 *   Both orderings are safe. This is the same idiom used by assets/customize-popup.js
 *   for x-drawer.
 *
 * ── Double-registration guard ─────────────────────────────────────────────────
 *
 *   If buy-buttons.liquid is rendered more than once on the same page (e.g. main
 *   product section + quick view), the <script defer> tag appears twice. The
 *   !customElements.get guard prevents a DOMException from a duplicate define call.
 */

customElements.whenDefined('variant-inventory').then(() => {
  if (!customElements.get('customize-button-variants')) {
    customElements.define(
      'customize-button-variants',
      class extends customElements.get('variant-inventory') {}
    );
  }
});
